/**
 * useDeviceTimezoneSync - saves the device's IANA timezone to the user's profile
 *
 * Reminder pushes are scheduled in the recipient's local time, so the server needs to know it.
 * After login / app open, compare the device timezone with the one on the profile (from
 * /auth/me) and report it only when it differs. No UI, and failures are silent — the server
 * falls back to a default timezone until a report lands, and the next app open retries.
 *
 * Skipped while an admin is impersonating: the admin's device timezone is not the user's.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { updateTimezone } from '@/services/authService';

/** The device's IANA timezone, or null when the runtime can't tell us. */
export function getDeviceTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function useDeviceTimezoneSync(): void {
  const { user, isAuthenticated, impersonator } = useAuth();
  const userId = user?.id;
  const savedTimezone = user?.timezone;
  // The profile in context isn't refreshed after a report, so remember what was already sent
  // for this user to avoid re-sending it on every re-render of the auth state.
  const reportedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !userId || impersonator) return;

    const deviceTimezone = getDeviceTimezone();
    if (!deviceTimezone || deviceTimezone === savedTimezone) return;

    const key = `${userId}:${deviceTimezone}`;
    if (reportedRef.current === key) return;
    reportedRef.current = key;

    updateTimezone(deviceTimezone).catch(() => {
      reportedRef.current = null;
    });
  }, [isAuthenticated, userId, savedTimezone, impersonator]);
}
